/**
 * Custom pickleball-paddle spec icons. Each icon is a paddle silhouette (or a
 * relevant cross-section) overlaid with dimension arrows that illustrate the
 * specific measurement (length, width, thickness, grip circumference, etc.).
 * All strokes use currentColor so the parent can tint them.
 */

const SvgBase = ({ children, className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    {children}
  </svg>
);

// Front-on paddle silhouette: oval face + handle
const PaddleBody = () => (
  <>
    <ellipse cx="12" cy="8.5" rx="5" ry="6" />
    <path d="M10.7 14.2v5.6c0 .8.6 1.4 1.3 1.4s1.3-.6 1.3-1.4v-5.6" />
  </>
);

// Overall paddle length — vertical arrow spanning the whole paddle (left side)
export const PaddleLengthIcon = ({ className }) => (
  <SvgBase className={className}>
    <PaddleBody />
    <path d="M3.4 4.6V19.8" />
    <path d="M2.2 5.8 3.4 4.6 4.6 5.8" />
    <path d="M2.2 18.6 3.4 19.8 4.6 18.6" />
  </SvgBase>
);

// Handle length — short vertical arrow along the handle (left side)
export const HandleLengthIcon = ({ className }) => (
  <SvgBase className={className}>
    <PaddleBody />
    <path d="M3.4 14.6V19.8" />
    <path d="M2.2 15.8 3.4 14.6 4.6 15.8" />
    <path d="M2.2 18.6 3.4 19.8 4.6 18.6" />
  </SvgBase>
);

// Surface width — horizontal arrow across the face
export const SurfaceWidthIcon = ({ className }) => (
  <SvgBase className={className}>
    <PaddleBody />
    <path d="M7.6 8.5h8.8" />
    <path d="M8.8 7.3 7.6 8.5 8.8 9.7" />
    <path d="M15.2 7.3 16.4 8.5 15.2 9.7" />
  </SvgBase>
);

// Surface length — vertical arrow over the face only
export const SurfaceLengthIcon = ({ className }) => (
  <SvgBase className={className}>
    <PaddleBody />
    <path d="M12 3.4V13.6" />
    <path d="M10.8 4.6 12 3.4 13.2 4.6" />
    <path d="M10.8 12.4 12 13.6 13.2 12.4" />
  </SvgBase>
);

// Grip circumference — handle cross-section circle wrapped by a circular arrow
export const GripCircumferenceIcon = ({ className }) => (
  <SvgBase className={className}>
    <circle cx="12" cy="12.5" r="4.4" />
    <path d="M12 4.2a8.3 8.3 0 1 1-6.2 2.8" />
    <path d="M4.7 4.2 5.6 6.9 8.4 6.4" />
  </SvgBase>
);

// Thickness — edge-on side profile with a horizontal thickness arrow
export const ThicknessIcon = ({ className }) => (
  <SvgBase className={className}>
    <rect x="10.4" y="3" width="3.2" height="12.4" rx="1.5" />
    <path d="M10.8 15.4v4.4c0 .7.5 1.2 1.2 1.2s1.2-.5 1.2-1.2v-4.4" />
    <path d="M10.4 8h3.2" />
    <path d="M11.6 6.8 10.4 8 11.6 9.2" />
    <path d="M12.4 6.8 13.6 8 12.4 9.2" />
  </SvgBase>
);

// Weight — paddle with a downward (gravity) arrow onto a baseline
export const WeightIcon = ({ className }) => (
  <SvgBase className={className}>
    <PaddleBody />
    <path d="M3.6 5v9.4" />
    <path d="M2.4 13.2 3.6 14.4 4.8 13.2" />
    <path d="M1.9 17.6h3.4" />
  </SvgBase>
);

// Core — paddle face with a honeycomb cell to denote the core structure
export const CoreIcon = ({ className }) => (
  <SvgBase className={className}>
    <PaddleBody />
    <path d="M12 5.6 14.1 6.85v2.5L12 10.6 9.9 9.35v-2.5z" />
  </SvgBase>
);
